import { forwardRef, useCallback, useRef, useState } from "react";
import classNames from "classnames";
import mergeRefs from "merge-refs";
import TextareaAutosize, { TextareaAutosizeProps } from "react-textarea-autosize";
import { DictType, ScenarioAutocompletes } from "@/model";
import InputAutocompleteMenu from "@/component/input/InputAutocompleteMenu";


interface TextareaProps extends Omit<TextareaAutosizeProps, "onChange"> {
    size?: "sm" | "md";
    autocompletes?: DictType<ScenarioAutocompletes>;
    onChange: (value: string, e?: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>((props, propsRef) => {
    const { size, autocompletes, value: propsValue, defaultValue: propsDefaultValue, ...rest } = props;

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [autocompleteTargetText, setAutocompleteTargetText] = useState<string>((propsValue ?? propsDefaultValue ?? "") as string);

    const validateRequired = useCallback((value: string) => (!!rest.required && !value.trim()), [rest.required]);
    const [isInvalidRequired, setIsInvalidRequired] = useState<boolean>(validateRequired(propsDefaultValue as string ?? propsValue as string ?? ""));

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setAutocompleteTargetText(value);
        setIsInvalidRequired(validateRequired(value));
        rest.onChange?.(value, e);
    };

    const onSelectAutocomplete = (value: string) => {
        if (!textareaRef.current) {
            return;
        }
        setAutocompleteTargetText(value);
        rest.onChange?.(value);
    };

    return (<>
        <TextareaAutosize ref={mergeRefs(textareaRef, propsRef)}
                          {...rest}
                          value={propsValue}
                          defaultValue={propsDefaultValue}
                          cacheMeasurements
                          aria-invalid={(!rest.disabled && isInvalidRequired) ? true : undefined}
                          className={classNames(
                              "input-default",
                              size === "sm" && "input-sm",
                              rest.className,
                              (!rest.disabled && isInvalidRequired) && "input-invalid",
                              rest.disabled && "resize-none cursor-not-allowed"
                          )}
                          onChange={onChange}
        />
        {autocompletes && (
            <InputAutocompleteMenu targetRef={textareaRef}
                                   targetText={autocompleteTargetText}
                                   autocompletes={autocompletes}
                                   onSelect={onSelectAutocomplete}
            />
        )}
    </>);
});

export default Textarea;
